import { useState, useEffect } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { parseOklch, formatOklch, type OklchColor, oklchToHex, hexToOklch } from "@/lib/colorUtils";

interface ColorPickerProps {
  value: string; // OKLCH string
  onChange: (oklchColor: string) => void;
}

export function ColorPicker({ value, onChange }: ColorPickerProps) {
  const [mode, setMode] = useState<"oklch" | "hex">("oklch");
  const [hexValue, setHexValue] = useState("");
  const color = parseOklch(value);
  
  useEffect(() => {
    setHexValue(oklchToHex(value));
  }, [value]);
  
  const updateColor = (updates: Partial<OklchColor>) => {
    const newColor = { ...color, ...updates };
    onChange(formatOklch(newColor));
  };

  const handleHexChange = (hex: string) => {
    setHexValue(hex);
    if (hex.match(/^#[0-9A-Fa-f]{6}$/)) {
      onChange(hexToOklch(hex));
    }
  };

  return (
    <div className="space-y-4">
      {/* Color Preview */}
      <div className="flex items-center gap-3">
        <div
          className="w-20 h-20 rounded-lg border-2 border-border shadow-xs"
          style={{ backgroundColor: value }}
        />
        <div className="flex-1">
          <div className="text-xs font-mono text-muted-foreground">
            {value}
          </div>
        </div>
      </div>

      <Tabs value={mode} onValueChange={(v) => setMode(v as "oklch" | "hex")}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="oklch">OKLCH</TabsTrigger>
          <TabsTrigger value="hex">Hex</TabsTrigger>
        </TabsList>

        <TabsContent value="oklch" className="space-y-4 mt-4">
          {/* Lightness */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Lightness</Label>
              <span className="text-xs text-muted-foreground font-mono">
                {color.l.toFixed(2)}
              </span>
            </div>
            <Slider
              value={[color.l]}
              onValueChange={([l]) => updateColor({ l })}
              min={0}
              max={1}
              step={0.01}
              className="w-full"
            />
          </div>

          {/* Chroma */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Chroma</Label>
              <span className="text-xs text-muted-foreground font-mono">
                {color.c.toFixed(2)}
              </span>
            </div>
            <Slider
              value={[color.c]}
              onValueChange={([c]) => updateColor({ c })}
              min={0}
              max={0.4}
              step={0.01}
              className="w-full"
            />
          </div>

          {/* Hue */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Hue</Label>
              <span className="text-xs text-muted-foreground font-mono">
                {color.h.toFixed(0)}°
              </span>
            </div>
            <Slider
              value={[color.h]}
              onValueChange={([h]) => updateColor({ h })}
              min={0}
              max={360}
              step={1}
              className="w-full"
            />
            {/* Hue gradient bar */}
            <div 
              className="w-full h-2 rounded-full"
              style={{
                background: `linear-gradient(to right, 
                  oklch(0.65 0.2 0), 
                  oklch(0.65 0.2 60), 
                  oklch(0.65 0.2 120), 
                  oklch(0.65 0.2 180), 
                  oklch(0.65 0.2 240), 
                  oklch(0.65 0.2 300), 
                  oklch(0.65 0.2 360)
                )`
              }}
            />
          </div>
        </TabsContent>

        <TabsContent value="hex" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-sm">Hex Color</Label>
            <Input
              type="text"
              placeholder="#000000"
              value={hexValue}
              onChange={(e) => handleHexChange(e.target.value)}
              className="font-mono"
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
